// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld-base';
import 'myWorld/uiComponents/form/userGroupSelector';

const layerScaleOptions = _.times(31, i => i);

myw.LayerEditor = myw.Form.extend({
    messageGroup: 'DefineLayerControl',
    className: 'ui-form layer-editor-form',
    tabs: [
        {
            id: 'properties-layers-tab',
            title: '{:properties}'
        },
        {
            id: 'general-layers-tab',
            title: '{:general}'
        }
    ],

    render() {
        this.generalRows = [
            {
                label: '{:visibility}:',
                components: [
                    new myw.Label({
                        label: '{:visibility_spacer}',
                        wrap: new myw.Dropdown({
                            name: 'min_scale',
                            type: 'number',
                            options: layerScaleOptions,
                            selected: 1
                        })
                    }),
                    new myw.Dropdown({
                        name: 'max_scale',
                        type: 'number',
                        options: layerScaleOptions,
                        selected: 20
                    })
                ]
            },
            {
                name: 'transparency',
                label: '{:transparency}:',
                components: [
                    new myw.Label({
                        label: '{:%}',
                        wrap: new myw.Input({
                            name: 'transparency',
                            type: 'number',
                            value: 0,
                            min: 0,
                            max: 100,
                            step: 5
                        })
                    }),
                    new myw.Label({
                        label: '{:control_item_transparency}',
                        cssClass: 'checkboxField',
                        wrap: new myw.Checkbox({
                            name: 'control_item_transparency'
                        })
                    })
                ]
            },
            {
                name: 'sharing',
                label: '{:sharing}:',
                components: [
                    new myw.UserGroupSelector({ system: this.options.system, name: 'sharing' })
                ]
            }
        ];

        this.$el.empty();

        this.propertiesTable = $('<table>', { class: 'layer-editor-properties' });
        this.advancedTable = $('<table>', { class: 'layer-editor-advanced' });
        this.generalTable = $('<table>', { class: 'layer-editor-general' });

        this.propertiesTabContent = $('<div>').append(this.propertiesTable);

        this.$el
            .append(this.propertiesTabContent)
            .append(this.generalTable)
            .append(this.advancedTable);

        this.$el.append('<div class="message-container"></div>'); // To show success messages and errors

        this.renderTabs();
        this.renderHeading(this.propertiesTable);
        this.renderGeneral(this.generalTable);
        this.renderAdvanced(this.advancedTable.hide());
        this.generalTable.hide();

        myw.translate(this.messageGroup, this.$el);
    },

    /**
     * Renders tabs to structure content
     */
    renderTabs() {
        const tabsDivId = 'layer-editor-tabs';
        this.$el.prepend($('<div>', { id: tabsDivId }));

        this.tabControl = new myw.TabControl(this, {
            el: this.$(`#${tabsDivId}`),
            tabs: this.tabs,
            initialTab: this.tabs[0].id
        });

        // Add a top border to the tab buttons
        this.tabControl._tabButtons.addClass('top-bordered');
        this.tabControl.on('change', tabId => {
            this.showTabContent();
        });

        this.tabControl.tabs['properties-layers-tab'].div.css('display', 'block');
    },

    showTabContent() {
        const currentTabId = this.tabControl.currentTabId;

        switch (currentTabId) {
            case 'properties-layers-tab':
                this.generalTable.hide();
                this.advancedTable.hide();
                this.propertiesTabContent.show();
                break;
            case 'general-layers-tab':
                this.propertiesTabContent.hide();
                this.advancedTable.hide();
                this.generalTable.show();
                break;
            case 'advanced-layers-tab':
                this.generalTable.hide();
                this.propertiesTabContent.hide();
                this.advancedTable.show();
                break;
        }
    },

    renderHeading(table) {
        let rows = [];

        const dsTypeInput = new myw.Dropdown({
            name: 'dsType',
            options: [
                { id: 'kml', label: this.msg('kml') },
                { id: 'generic_tiles', label: this.msg('generic_tiles') },
                { id: 'esri', label: this.msg('esri') },
                { id: 'ogc', label: this.msg('ogc') }
            ],
            selected: this.dsType,
            onChange: () => this.options.typeChangeCallback()
        });

        const nameOptions = { name: 'name', size: '40' };
        if (this.options.id)
            Object.assign(nameOptions, { cssClass: 'disabled-input', disabled: 'disabled' });

        rows = [
            {
                label: '{:owner}:',
                components: [
                    new myw.Input({
                        name: 'owner',
                        cssClass: 'disabled-input',
                        disabled: 'disabled'
                    })
                ]
            },
            {
                label: '{:name}:',
                components: [new myw.Input({ ...nameOptions })]
            },
            {
                label: '{:url}:',
                components: [
                    new myw.Input({
                        name: 'url',
                        cssClass: 'block',
                        onChange: () => this.onUrlChange()
                    })
                ]
            },
            {
                label: '{:dsType}:',
                components: [dsTypeInput]
            }
        ];

        this._renderRows(table, rows);
        if (!this.options.id) this.setValue('owner', myw.currentUser.username);
    },

    renderGeneral(table) {
        this._renderRows(table, this.generalRows);
    },

    renderAdvanced(table) {},

    setValuesFrom(layerDef) {
        const values = this.layerDefToFormValues(layerDef);
        this.setValues(values);
    },

    getLayerDef() {
        return this.formValuesToLayerDef(this.getValues());
    },

    disable() {
        Object.keys(this.formInputs).forEach(input => {
            this.formInputs[input].addAttribute('disabled', true);
            this.formInputs[input].$el.find('.ui-input, .ui-select').addClass('disabled-input');
        });
    },

    formValuesToLayerDef(formValues) {
        const names = ['name', 'url', 'min_scale', 'max_scale', 'control_item_transparency'].concat(
            this.generalRows.map(row => row.name)
        );
        let def = {
            ..._.pick(formValues, names),
            datasource_spec: {
                type: formValues.dsType
            }
        };
        this._applyWidgetControlsToDef(def, formValues);
        return def;
    },

    layerDefToFormValues(layerDef) {
        let ds = layerDef.datasource_spec;
        let def = {
            dsType: ds.type,
            ...layerDef,
            ...ds
        };
        this._applyWidgetControlsToFormValues(def, layerDef);
        return def;
    },

    //to be subclassed
    onUrlChange() {},

    _applyWidgetControlsToFormValues(def, layerDef) {
        const legendWidgets = [
            'myw.EsriLegendLayerControlWidget',
            'myw.OgcLegendLayerControlWidget'
        ];
        const genericWidgets = ['myw.TransparencyLayerControlWidget'];
        const classes = (layerDef.control_item_class || '')
            .replace('[', '')
            .replace(']', '')
            .split(',');
        let control_item_legend = false;
        let control_item_transparency = false;

        classes.forEach(namespace => {
            if (!control_item_legend && legendWidgets.includes(namespace))
                control_item_legend = true;
            if (!control_item_transparency && genericWidgets.includes(namespace))
                control_item_transparency = true;
        });
        def.control_item_legend = control_item_legend;
        def.control_item_transparency = control_item_transparency;
    },

    _applyWidgetControlsToDef(def, formValues) {
        const { control_item_legend, control_item_transparency, dsType } = formValues;
        if (undefined === control_item_legend && undefined === control_item_transparency) return;

        let path = [];
        switch (dsType) {
            case 'esri':
                if (control_item_legend) path.push('myw.EsriLegendLayerControlWidget');
                break;
            case 'ogc':
                if (control_item_legend) path.push('myw.OgcLegendLayerControlWidget');
                break;
        }
        if (control_item_transparency) path.push('myw.TransparencyLayerControlWidget');
        //replace the boolean fields with the control_item_class array
        def.control_item_class = `[${path.toString()}]`;
        delete def.control_item_legend;
        delete def.control_item_transparency;
    },

    displayMessage(message, type) {
        new myw.DisplayMessage({ el: this.$('.message-container'), type: type, message: message });
    }
});

myw.layerEditors = {};

export default myw.LayerEditor;
